import React, { useEffect, useState } from 'react';
import { adminFetch } from '../lib/extras';
import { defaultAiIdeasFor, type SetIdea } from '../lib/aiIdeas';
import { toCsv, toXlsx, toXml, download } from '../lib/exportTable';

// Administrace → Témata: verze webu na vlastní cestě (/pejsci, /kocicky …) v jedné aplikaci.
// Každé téma má vlastní nastavení a knihovnu (upravují se na adrese tématu) a vlastní složku objednávek.

const box = 'p-4 bg-gray-50 border-2 border-gray-300 rounded-md';
const label = 'text-[10px] font-black text-gray-700 uppercase tracking-widest block';
const hint = 'text-[9px] font-bold text-gray-400 block mt-1';
const txt = 'w-full px-3 py-2 bg-white border-2 border-gray-300 rounded-md text-xs font-bold outline-none';
const btn = 'px-4 py-2 bg-white border-2 border-gray-300 rounded-md text-[10px] font-black uppercase tracking-widest hover:border-gray-500 disabled:opacity-50';
const btnDark = 'px-4 py-2 bg-black text-white border-2 border-black rounded-md text-[10px] font-black uppercase tracking-widest hover:bg-gray-800 disabled:opacity-50';

interface Theme {
  slug: string; name: string; enabled: boolean; instanceId: string;
  driveFolderId: string; driveOk: boolean | null; driveCheckedAt: string | null;
  seoTitle: string; seoDescription: string; seoH1?: string; seoKeywords?: string; seoByLang?: Record<string, SeoEntry>; ai?: { ideas?: string[]; sets?: SetIdea[]; photoHint?: string }; copiedFrom: string; createdAt: string | null; createdBy: string;
}
// Nápady pro AI (rychlé volby v okně „Celá samolepka s AI“) – prázdné pole = výchozí nápady podle tématu.
const AiIdeasEditor: React.FC<{ t: Theme; busy: boolean; onSave: (ai: { ideas: string[]; sets: SetIdea[]; photoHint: string }) => void }> = ({ t, busy, onSave }) => {
  const def = defaultAiIdeasFor(t.slug, t.name);
  const toSets = (v: SetIdea[] = []) => v.map(x => `${x.label}: ${x.items}`).join('\n');
  const [ideas, setIdeas] = useState((t.ai?.ideas || []).join('\n'));
  const [sets, setSets] = useState(toSets(t.ai?.sets));
  const [photo, setPhoto] = useState(t.ai?.photoHint || '');
  const parsed = {
    ideas: ideas.split('\n').map(x => x.trim()).filter(Boolean).slice(0, 8),
    sets: sets.split('\n').map(l => { const i = l.indexOf(':'); return i > 0 ? { label: l.slice(0, i).trim(), items: l.slice(i + 1).trim() } : null; })
      .filter((x): x is SetIdea => !!x && !!x.label && !!x.items).slice(0, 6),
    photoHint: photo.trim(),
  };
  const area = 'w-full mt-1 px-3 py-2 border-2 border-gray-200 rounded-md text-xs font-bold focus:border-black outline-none';
  return (
    <div>
      <span className="block text-[10px] font-black uppercase tracking-widest text-gray-500">Nápady pro AI (okno „Celá samolepka s AI“)</span>
      <span className="block text-[11px] text-gray-500 mt-1">Prázdné pole = {def ? 'výchozí nápady pro toto téma (šedě v poli)' : 'obecné nápady hlavní adresy'}.</span>
      <label className="block mt-2 text-[11px] font-bold">Jedna samolepka – nápady (každý na řádek, max. 8)</label>
      <textarea className={area} rows={4} value={ideas} onChange={e => setIdeas(e.target.value)} placeholder={(def?.ideas || []).join('\n')} />
      <label className="block mt-2 text-[11px] font-bold">Sada – rychlé volby (řádek „Název: motiv, motiv, motiv“, max. 6)</label>
      <textarea className={area} rows={4} value={sets} onChange={e => setSets(e.target.value)} placeholder={toSets(def?.sets)} />
      <label className="block mt-2 text-[11px] font-bold">Podle fotky – příklad doplnění</label>
      <input className={area} value={photo} maxLength={80} onChange={e => setPhoto(e.target.value)} placeholder={def?.photoHint || ''} />
      <button type="button" className="mt-2 px-3 py-2 rounded-md border-2 border-black text-[11px] font-black uppercase disabled:opacity-40" disabled={busy} onClick={() => onSave(parsed)}>Uložit nápady</button>
    </div>
  );
};

type SeoEntry = { title?: string; description?: string; h1?: string; keywords?: string };
type SeoFields = { seoTitle?: string; seoDescription?: string; seoH1?: string; seoKeywords?: string; seoByLang?: Record<string, SeoEntry> };

// Název → adresa: malá písmena bez diakritiky, pomlčky.
const toSlug = (s: string) => s.normalize('NFD').replace(/[\u0300-\u036f]/g, '').toLowerCase()
  .replace(/[^a-z0-9]+/g, '-').replace(/^-+|-+$/g, '').slice(0, 40);

const SEO_TABS: [string, string][] = [['cs', 'CZ'], ['sk', 'SK'], ['pl', 'PL'], ['de', 'DE'], ['en', 'EN'], ['it', 'IT'], ['uk', 'UA'], ['hu', 'HU']];
const LANG_LABEL: Record<string, string> = Object.fromEntries(SEO_TABS);
const BRANDS: Record<string, string> = { pl: 'AAAkalendarze', de: 'AAAkalender' };

// Uložená pole → mapa jazyků (čeština ze seoTitle…) a zpět.
const toMap = (f: SeoFields): Record<string, SeoEntry> => ({
  ...(f.seoByLang || {}),
  cs: { title: f.seoTitle || '', description: f.seoDescription || '', h1: f.seoH1 || '', keywords: f.seoKeywords || '' },
});
const fromMap = (m: Record<string, SeoEntry>): SeoFields => {
  const seoByLang: Record<string, SeoEntry> = {};
  for (const [l] of SEO_TABS) if (l !== 'cs' && m[l]) seoByLang[l] = m[l];
  return { seoTitle: m.cs?.title || '', seoDescription: m.cs?.description || '', seoH1: m.cs?.h1 || '', seoKeywords: m.cs?.keywords || '', seoByLang };
};
const norm = (m: Record<string, SeoEntry>) => JSON.stringify(SEO_TABS.map(([l]) => ['title', 'description', 'h1', 'keywords'].map(f => ((m[l] as any)?.[f] || '').trim())));

// Texty pro vyhledávače po jazycích: Title, Description, H1, Keywords.
const SeoEditor: React.FC<{ initial: SeoFields; busy: boolean; onSave: (f: SeoFields) => void }> = ({ initial, busy, onSave }) => {
  const [map, setMap] = useState<Record<string, SeoEntry>>(() => toMap(initial));
  const [tab, setTab] = useState('cs');
  const dirty = norm(map) !== norm(toMap(initial));
  const cur = map[tab] || {};
  const set = (f: keyof SeoEntry, v: string) => setMap(m => ({ ...m, [tab]: { ...(m[tab] || {}), [f]: v } }));
  return (
    <div>
      <span className={label}>Vyhledávače a sdílení (SEO)</span>
      <div className="flex gap-1 flex-wrap mt-1" role="tablist" aria-label="Jazyk textů">
        {SEO_TABS.map(([l, lbl]) => (
          <button key={l} type="button" role="tab" aria-selected={tab === l} onClick={() => setTab(l)} className={`px-2 py-1 border-2 rounded text-[10px] font-black ${tab === l ? 'bg-black text-white border-black' : 'bg-white border-gray-300'}`}>{lbl}{map[l]?.title ? '' : ' ·'}</button>
        ))}
      </div>
      <label className={`${hint} mt-2`}>SEO – Title</label>
      <input className={txt} value={cur.title || ''} maxLength={120} data-seo-lang={tab} data-seo-field="title" onChange={e => set('title', e.target.value)} placeholder="Titulek stránky bez značky, např. Samolepky s pejsky na míru se jménem" />
      <label className={`${hint} mt-2`}>SEO – Description · {(cur.description || '').length} znaků (do 160 je ideální)</label>
      <textarea className={txt} rows={2} value={cur.description || ''} maxLength={300} data-seo-lang={tab} data-seo-field="description" onChange={e => set('description', e.target.value)} placeholder="Popis stránky pro Google" />
      <label className={`${hint} mt-2`}>SEO – H1</label>
      <input className={txt} value={cur.h1 || ''} maxLength={120} data-seo-lang={tab} data-seo-field="h1" onChange={e => set('h1', e.target.value)} placeholder="Hlavní nadpis stránky pro vyhledávače, např. Samolepky se jménem a pejskem" />
      <label className={`${hint} mt-2`}>SEO – Keywords (oddělte čárkou)</label>
      <input className={txt} value={cur.keywords || ''} maxLength={300} data-seo-lang={tab} data-seo-field="keywords" onChange={e => set('keywords', e.target.value)} placeholder="samolepky se jménem, jmenovky do školky, samolepky pejsek" />
      <div className="flex justify-between items-center mt-1 gap-2">
        <span className={hint}>Značka se k titulku doplní sama podle jazyka (AAApapir, PL AAAkalendarze, DE AAAkalender) · H1 je skrytý nadpis pro vyhledávače, viditelný název v hlavičce se nemění · chybějící jazyk = angličtina, pak čeština · tečka u jazyka = bez titulku</span>
        <button type="button" className={btn} disabled={busy || !dirty} onClick={() => onSave(fromMap(map))}>Uložit</button>
      </div>
    </div>
  );
};

// SEO hlavní adresy (verze bez cesty).
const MainSeoCard: React.FC<{ onChanged: () => void }> = ({ onChanged }) => {
  const [data, setData] = useState<SeoFields | null>(null);
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState<{ ok: boolean; text: string } | null>(null);
  const load = () => adminFetch<SeoFields>('/api/admin/site-seo').then(setData).catch(e => setMsg({ ok: false, text: e.message }));
  useEffect(() => { load(); }, []);
  const save = async (f: SeoFields) => {
    setBusy(true); setMsg(null);
    try { await adminFetch('/api/admin/site-seo', { body: f }); setMsg({ ok: true, text: 'Texty pro vyhledávače uloženy (projeví se do 30 s).' }); await load(); onChanged(); }
    catch (e: any) { setMsg({ ok: false, text: e.message }); }
    finally { setBusy(false); }
  };
  const url = window.location.origin + '/';
  return (
    <div className={box}>
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div className="min-w-0">
          <span className={label}>Hlavní adresa</span>
          <a href={url} target="_blank" rel="noreferrer" className="text-[11px] font-bold font-mono text-blue-700 break-all">{url}</a>
          <span className={hint}>Návratová adresa a ostatní nastavení se upravují v sekcích administrace na této adrese.</span>
        </div>
        <button type="button" className={btn} onClick={() => setOpen(o => !o)}>{open ? 'Zavřít' : 'SEO'}</button>
      </div>
      {open && data && <div className="mt-4 border-t-2 border-gray-200 pt-4"><SeoEditor key={JSON.stringify(data)} initial={data} busy={busy} onSave={save} /></div>}
      {msg && <p className={`mt-2 text-[11px] font-bold ${msg.ok ? 'text-green-700' : 'text-red-700'}`}>{msg.text}</p>}
    </div>
  );
};

// Přehled verzí s exportem: řádek = verze (a jazyk), adresa, návratová URL, SEO, založení, čas stažení.
interface ExportRow { slug: string; name: string; instanceId: string; enabled: boolean; createdAt: string | null; returnUrl: string; returnUrlByLang: Record<string, string>; seo: Record<string, SeoEntry> | null }
const COLS: [string, string][] = [
  ['verze', 'Verze'], ['jazyk', 'Jazyk'], ['url', 'URL'], ['navratova_adresa', 'Návratová adresa'],
  ['seo_description', 'SEO - Description'], ['seo_h1', 'SEO - H1'], ['seo_keywords', 'SEO - Keywords'], ['seo_title', 'SEO - Title'],
  ['zalozeno', 'Datum a čas založení'], ['stazeno', 'Čas stažení dat'],
];
const pad = (n: number) => String(n).padStart(2, '0');
const fmt = (iso: string | null) => { if (!iso) return ''; const d = new Date(iso); return isNaN(+d) ? '' : `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())} ${pad(d.getHours())}:${pad(d.getMinutes())}:${pad(d.getSeconds())}`; };
const pick = (seo: Record<string, SeoEntry> | null, l: string) => {
  const order = l === 'cs' ? ['cs'] : l === 'sk' ? ['sk', 'cs'] : [l, 'en', 'cs'];
  const out: SeoEntry = {};
  for (const k of order) for (const f of ['title', 'description', 'h1', 'keywords'] as const) if (!out[f] && seo?.[k]?.[f]) out[f] = seo[k][f];
  return out;
};

const VersionsTable: React.FC<{ reloadKey: number }> = ({ reloadKey }) => {
  const [data, setData] = useState<{ rows: ExportRow[]; langs: string[] } | null>(null);
  const [err, setErr] = useState<string | null>(null);
  const [lang, setLang] = useState('cs');
  const [busy, setBusy] = useState('');
  const load = () => adminFetch('/api/admin/themes/export').then(d => { setData(d); setErr(null); return d; }).catch(e => { setErr(e.message); return null; });
  useEffect(() => { load(); }, [reloadKey]);

  const build = (d: { rows: ExportRow[]; langs: string[] }, stamp: string) => {
    const langs = lang === 'all' ? d.langs : [lang];
    const origin = window.location.origin;
    const rows: string[][] = [];
    for (const r of d.rows) for (const l of langs) {
      const e = pick(r.seo, l);
      const title = e.title ? (/AAA[a-z]/i.test(e.title) ? e.title : `${e.title} | ${BRANDS[l] || 'AAApapir'}`) : '';
      rows.push([
        r.name + (r.enabled ? '' : ' (vypnuto)'), LANG_LABEL[l] || l,
        `${origin}/${r.slug}${l === 'cs' ? '' : `?lang=${l}`}`,
        r.returnUrlByLang?.[l] || r.returnUrl || '',
        e.description || '', e.h1 || '', e.keywords || '', title,
        fmt(r.createdAt), stamp,
      ]);
    }
    return { columns: COLS.map(c => c[1]), rows, sheetName: 'Verze webu' };
  };

  const exportAs = async (kind: 'xlsx' | 'csv' | 'xml') => {
    setBusy(kind);
    try {
      const d = await load(); // vždy aktuální data
      if (!d) return;
      const now = new Date();
      const t = build(d, fmt(now.toISOString()));
      const name = `verze-webu_${window.location.hostname.split('.')[0]}_${fmt(now.toISOString()).replace(/[: ]/g, '-')}`;
      if (kind === 'xlsx') download(toXlsx(t), `${name}.xlsx`);
      else if (kind === 'csv') download(toCsv(t), `${name}.csv`);
      else download(toXml(t, COLS.map(c => c[0])), `${name}.xml`);
    } finally { setBusy(''); }
  };

  const preview = data ? build(data, '(doplní se při stažení)') : null;
  return (
    <div className={box}>
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <span className={label}>Přehled verzí a export</span>
        <div className="flex gap-2 flex-wrap items-center">
          <select className="px-2 py-2 bg-white border-2 border-gray-300 rounded-md text-[10px] font-black" value={lang} onChange={e => setLang(e.target.value)} aria-label="Jazyk">
            {SEO_TABS.map(([l, lbl]) => <option key={l} value={l}>{lbl}</option>)}
            <option value="all">Všechny jazyky</option>
          </select>
          <button type="button" className={btnDark} disabled={!!busy || !data} onClick={() => exportAs('xlsx')}>{busy === 'xlsx' ? 'Připravuji…' : 'Excel'}</button>
          <button type="button" className={btn} disabled={!!busy || !data} onClick={() => exportAs('csv')}>CSV</button>
          <button type="button" className={btn} disabled={!!busy || !data} onClick={() => exportAs('xml')}>XML</button>
        </div>
      </div>
      {err && <p className="mt-2 text-[11px] font-bold text-red-700">{err}</p>}
      {!data && !err && <p className="mt-2 text-[11px] text-gray-500">Načítám…</p>}
      {preview && (
        <div className="mt-3 overflow-x-auto border-2 border-gray-200 rounded-md bg-white">
          <table className="min-w-full text-[10px]" data-versions-table="">
            <thead className="bg-gray-100"><tr>{preview.columns.map(c => <th key={c} className="px-2 py-1.5 text-left font-black uppercase tracking-wider whitespace-nowrap">{c}</th>)}</tr></thead>
            <tbody>{preview.rows.map((r, i) => <tr key={i} className="border-t border-gray-200 align-top">{r.map((v, j) => <td key={j} className={`px-2 py-1.5 ${j === 2 || j === 3 ? 'font-mono break-all min-w-[200px]' : j >= 4 && j <= 7 ? 'min-w-[180px]' : 'whitespace-nowrap'}`}>{v || <span className="text-gray-300">—</span>}</td>)}</tr>)}</tbody>
          </table>
        </div>
      )}
      <span className={hint}>Řádek = verze webu{lang === 'all' ? ' a jazyk' : ''}. Excel (.xlsx), CSV (středník, UTF-8) i XML se stáhnou s aktuálními daty a časem stažení. Chybějící SEO jazyka se doplní jako na webu (angličtina, pak čeština).</span>
    </div>
  );
};


// Knihovna tématu: vlastní motivy + složky sdílené z hlavní adresy (odkazem, oprava se projeví všude).
type LibCat = { id: string; name: string; hidden: boolean; count: number };
type Shared = { iconCategories: string[]; backgrounds: boolean; shapes: boolean; templates: boolean };
interface LibInfo {
  shared: Shared; own: Record<string, number>; main: Record<string, number>; copies: Record<string, number>;
  ownCategories: LibCat[]; mainCategories: LibCat[];
}
const KIND_LABEL: [string, keyof Omit<Shared, 'iconCategories'> | null, string][] = [
  ['custom_icons', null, 'Ikony'], ['custom_backgrounds', 'backgrounds', 'Pozadí'], ['custom_shapes', 'shapes', 'Tvary'], ['custom_templates', 'templates', 'Šablony samolepek'],
];

const LibraryEditor: React.FC<{ slug: string; url: string }> = ({ slug, url }) => {
  const [info, setInfo] = useState<LibInfo | null>(null);
  const [draft, setDraft] = useState<Shared | null>(null);
  const [busy, setBusy] = useState('');
  const [msg, setMsg] = useState<{ ok: boolean; text: string } | null>(null);
  const [impName, setImpName] = useState('');
  const [impFolder, setImpFolder] = useState('');
  const [impFiles, setImpFiles] = useState<File[]>([]);
  const apply = (i: LibInfo) => { setInfo(i); setDraft(i.shared); };
  const load = () => adminFetch<LibInfo>(`/api/admin/themes/library?slug=${encodeURIComponent(slug)}`).then(apply).catch(e => setMsg({ ok: false, text: e.message }));
  useEffect(() => { load(); }, [slug]);
  if (!info || !draft) return <div><span className={label}>Knihovna</span><p className={hint}>{msg ? msg.text : 'Načítám…'}</p></div>;

  const dirty = JSON.stringify({ ...draft, iconCategories: [...draft.iconCategories].sort() }) !== JSON.stringify({ ...info.shared, iconCategories: [...info.shared.iconCategories].sort() });
  const toggleCat = (id: string) => setDraft(d => d && ({ ...d, iconCategories: d.iconCategories.includes(id) ? d.iconCategories.filter(x => x !== id) : [...d.iconCategories, id] }));
  const save = async () => {
    setBusy('save'); setMsg(null);
    try { await adminFetch('/api/admin/themes/library', { body: { slug, shared: draft } }); setMsg({ ok: true, text: 'Sdílení uloženo. Zákazníci ho uvidí po obnovení stránky tématu.' }); await load(); }
    catch (e: any) { setMsg({ ok: false, text: e.message }); } finally { setBusy(''); }
  };
  const sum = (m: Record<string, number>) => Object.keys(m).reduce((a, k) => a + (Number(m[k]) || 0), 0);
  const totalCopies = sum(info.copies);
  const removeCopies = async () => {
    if (!window.confirm(`Smazat z tématu ${totalCopies} položek, které jsou beze změny zkopírované z hlavní adresy?\n\nOriginály zůstanou na hlavní adrese a téma je bude sdílet (složky, ze kterých ikony zmizí, se automaticky zapnou ve sdílení). Upravené a vlastní položky tématu zůstanou.`)) return;
    setBusy('copies'); setMsg(null);
    try {
      const r = await adminFetch<{ removed: Record<string, number>; movedToShared: string[]; info: LibInfo }>('/api/admin/themes/library/remove-copies', { body: { slug } });
      apply(r.info);
      const n = sum(r.removed);
      setMsg({ ok: true, text: `Smazáno ${n} kopií, ${r.movedToShared.length} složek ikon se teď sdílí z hlavní adresy.` });
    } catch (e: any) { setMsg({ ok: false, text: e.message }); } finally { setBusy(''); }
  };
  const ownOnly = info.ownCategories.filter(c => c.count > 0);
  const runImport = async () => {
    if (!impName.trim() || (!impFolder.trim() && !impFiles.length)) return;
    setBusy('import'); setMsg(null);
    try {
      const files = impFolder.trim() ? undefined : await Promise.all(impFiles.map(f => new Promise<{ name: string; data: string }>((ok, ko) => {
        const r = new FileReader(); r.onload = () => ok({ name: f.name, data: String(r.result) }); r.onerror = () => ko(new Error(f.name)); r.readAsDataURL(f);
      })));
      const r = await adminFetch<{ imported: number; failed: string[]; category: { name: string }; info: LibInfo }>('/api/admin/themes/library/import', { body: { slug, categoryName: impName.trim(), folder: impFolder.trim() || undefined, files } });
      apply(r.info);
      setMsg({ ok: !r.failed.length, text: `Do složky „${r.category.name}“ nahráno ${r.imported} motivů${r.failed.length ? `, nepodařilo se: ${r.failed.join(', ')}` : ''}. Složka je v editoru tématu první.` });
      setImpFiles([]);
    } catch (e: any) { setMsg({ ok: false, text: e.message }); } finally { setBusy(''); }
  };

  return (
    <div data-theme-library={slug}>
      <span className={label}>Knihovna</span>
      <span className={hint}>
        Vlastní motivy a šablony tématu nahrávejte na <a className="text-blue-700" href={url} target="_blank" rel="noreferrer">adrese tématu</a> (Administrace → Knihovna) do vlastní složky.
        Zaškrtnuté složky hlavní adresy se k nim připojí odkazem – nekopírují se a jejich úprava na hlavní adrese se projeví ve všech tématech. V editoru jsou nejdřív vlastní složky, pak sdílené.
      </span>
      <div className="mt-2 text-[11px] text-gray-700">
        <strong>Vlastní:</strong> {KIND_LABEL.map(([c, , l]) => `${l} ${info.own[c] || 0}`).join(' · ')}
        {ownOnly.length > 0 && <span className="text-gray-500"> · složky ikon: {ownOnly.map(c => `${c.name} (${c.count})`).join(', ')}</span>}
      </div>
      <div className="mt-3 p-3 bg-white border-2 border-gray-200 rounded-md" data-theme-import={slug}>
        <span className={hint}>Nahrát vlastní motivy tématu (PNG, JPG nebo WebP; ořežou se, zmenší na 384 px a uloží s průhledností)</span>
        <div className="grid gap-2 mt-1 sm:grid-cols-2">
          <input className={txt} value={impName} maxLength={60} data-import-field="name" onChange={e => setImpName(e.target.value)} placeholder="Název složky, např. Pejsci" />
          <input className={`${txt} font-mono`} value={impFolder} data-import-field="folder" onChange={e => setImpFolder(e.target.value)} placeholder="odkaz na složku Google Drive s obrázky" />
        </div>
        <div className="flex items-center justify-between gap-2 mt-2 flex-wrap">
          <label className="text-[11px] font-bold">nebo soubory z počítače: <input type="file" multiple accept="image/png,image/jpeg,image/webp" disabled={!!impFolder.trim()} onChange={e => setImpFiles(Array.from(e.target.files || []))} /></label>
          <button type="button" className={btnDark} data-import-run="" disabled={!!busy || !impName.trim() || (!impFolder.trim() && !impFiles.length)} onClick={runImport}>{busy === 'import' ? 'Nahrávám…' : 'Nahrát motivy'}</button>
        </div>
        <span className={hint}>Název motivu = název souboru (bez čísla na začátku). Existující složka se stejným názvem se doplní; opakovaný import ze stejné složky Drive motivy přepíše. Složku Drive nasdílejte servisnímu účtu aplikace (stačí Čtenář).</span>
      </div>
      <div className="mt-3">
        <span className={hint}>Sdílené složky ikon z hlavní adresy</span>
        <div className="flex flex-wrap gap-2 mt-1">
          {info.mainCategories.map(c => (
            <label key={c.id} className={`flex items-center gap-1.5 px-2 py-1 border-2 rounded-md text-[10px] font-bold cursor-pointer ${draft.iconCategories.includes(c.id) ? 'bg-white border-black' : 'bg-gray-100 border-gray-300 text-gray-500'}`}>
              <input type="checkbox" data-share-category={c.id} checked={draft.iconCategories.includes(c.id)} onChange={() => toggleCat(c.id)} />
              {c.name} <span className="text-gray-400">({c.count}){c.hidden ? ' · skrytá na hlavní adrese' : ''}</span>
            </label>
          ))}
        </div>
        <div className="flex gap-2 mt-1">
          <button type="button" className="text-[10px] font-bold text-blue-700" onClick={() => setDraft(d => d && ({ ...d, iconCategories: info.mainCategories.filter(c => !c.hidden).map(c => c.id) }))}>Vybrat viditelné</button>
          <button type="button" className="text-[10px] font-bold text-blue-700" onClick={() => setDraft(d => d && ({ ...d, iconCategories: [] }))}>Zrušit výběr</button>
        </div>
      </div>
      <div className="mt-3 flex flex-wrap gap-3">
        {KIND_LABEL.filter(k => k[1]).map(([c, key, l]) => (
          <label key={c} className="flex items-center gap-1.5 text-[11px] font-bold">
            <input type="checkbox" data-share-kind={key!} checked={!!draft[key!]} onChange={e => setDraft(d => d && ({ ...d, [key!]: e.target.checked }))} />
            Sdílet {l.toLowerCase()} z hlavní adresy <span className="text-gray-400">({info.main[c] || 0})</span>
          </label>
        ))}
      </div>
      <div className="flex items-center justify-between gap-3 mt-3 flex-wrap">
        {totalCopies > 0
          ? <span className={hint}>V tématu je {totalCopies} položek beze změny zkopírovaných z hlavní adresy ({KIND_LABEL.filter(([c]) => info.copies[c]).map(([c, , l]) => `${l.toLowerCase()} ${info.copies[c]}`).join(', ')}).</span>
          : <span className={hint}>Téma nemá žádné nadbytečné kopie z hlavní adresy.</span>}
        <div className="flex gap-2">
          {totalCopies > 0 && <button type="button" className={btn} disabled={!!busy || dirty} title={dirty ? 'Nejdřív uložte sdílení' : ''} onClick={removeCopies}>{busy === 'copies' ? 'Čistím…' : 'Smazat kopie a sdílet originály'}</button>}
          <button type="button" className={btnDark} disabled={!!busy || !dirty} onClick={save}>{busy === 'save' ? 'Ukládám…' : 'Uložit sdílení'}</button>
        </div>
      </div>
      {msg && <p className={`mt-2 text-[11px] font-bold ${msg.ok ? 'text-green-700' : 'text-red-700'}`}>{msg.text}</p>}
    </div>
  );
};

const ThemeRow: React.FC<{ t: Theme; isSuper: boolean; serviceAccount: string; onChanged: () => void }> = ({ t, isSuper, serviceAccount, onChanged }) => {
  const [open, setOpen] = useState(false);
  const [name, setName] = useState(t.name);
  const [folder, setFolder] = useState(t.driveFolderId);
  const [busy, setBusy] = useState('');
  const [msg, setMsg] = useState<{ ok: boolean; text: string } | null>(null);
  const url = `${window.location.origin}/${t.slug}`;

  const save = async (body: Record<string, unknown>, what: string, okText: string) => {
    setBusy(what); setMsg(null);
    try { await adminFetch('/api/admin/themes/update', { body: { slug: t.slug, ...body } }); setMsg({ ok: true, text: okText }); onChanged(); }
    catch (e: any) { setMsg({ ok: false, text: e.message }); }
    finally { setBusy(''); }
  };
  const recheck = async () => {
    setBusy('check'); setMsg(null);
    try {
      const r = await adminFetch<{ ok: boolean; error?: string }>('/api/admin/themes/check-drive', { body: { slug: t.slug } });
      setMsg(r.ok ? { ok: true, text: 'Zápis do složky funguje.' } : { ok: false, text: r.error || 'Zápis se nepodařil.' });
      onChanged();
    } catch (e: any) { setMsg({ ok: false, text: e.message }); }
    finally { setBusy(''); }
  };

  const driveState = !t.driveFolderId
    ? <span className="text-orange-600">bez složky – objednávky jdou do náhradní složky _bez-slozky</span>
    : t.driveOk === false
      ? <span className="text-red-700">složka nefunguje – ověřte sdílení</span>
      : <span className="text-green-700">vlastní složka{t.driveCheckedAt ? ` · ověřeno ${new Date(t.driveCheckedAt).toLocaleString('cs-CZ')}` : ''}</span>;

  return (
    <div className={box}>
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div className="min-w-0">
          <span className={label}>{t.name} {!t.enabled && <span className="text-red-700">· vypnuto</span>}</span>
          <a href={url} target="_blank" rel="noreferrer" className="text-[11px] font-bold font-mono text-blue-700 break-all">{url}</a>
          <span className={hint}>Objednávky: {driveState}</span>
        </div>
        <div className="flex gap-2 flex-wrap">
          <a className={btnDark} href={url} target="_blank" rel="noreferrer">Otevřít a upravit</a>
          {isSuper && <button type="button" className={btn} onClick={() => setOpen(o => !o)}>{open ? 'Zavřít' : 'Nastavení'}</button>}
        </div>
      </div>
      {open && isSuper && (
        <div className="mt-4 space-y-4 border-t-2 border-gray-200 pt-4">
          <div>
            <span className={label}>Název tématu</span>
            <div className="flex gap-2 mt-1">
              <input className={txt} value={name} maxLength={80} onChange={e => setName(e.target.value)} />
              <button type="button" className={btn} disabled={!!busy || !name.trim() || name === t.name} onClick={() => save({ name }, 'name', 'Název uložen.')}>Uložit</button>
            </div>
            <span className={hint}>Interní název v administraci. Texty pro zákazníky (nadpis, logo) se upravují na adrese tématu v sekci Značka a texty.</span>
          </div>
          <div>
            <span className={label}>Složka objednávek (Google Drive)</span>
            <div className="flex gap-2 mt-1 flex-wrap sm:flex-nowrap">
              <input className={`${txt} font-mono`} value={folder} onChange={e => setFolder(e.target.value)} placeholder="odkaz na složku nebo její ID" />
              <button type="button" className={btn} disabled={!!busy || folder === t.driveFolderId} onClick={() => save({ driveFolderId: folder }, 'folder', folder ? 'Složka ověřena a uložena.' : 'Složka odebrána – objednávky půjdou do _bez-slozky.')}>{busy === 'folder' ? 'Ověřuji…' : 'Ověřit a uložit'}</button>
              {t.driveFolderId && <button type="button" className={btn} disabled={!!busy} onClick={recheck}>{busy === 'check' ? 'Ověřuji…' : 'Ověřit přístup'}</button>}
            </div>
            <span className={hint}>
              Složku nasdílejte servisnímu účtu {serviceAccount ? <strong className="font-mono">{serviceAccount}</strong> : 'aplikace'} jako Přispěvatel / Editor (doporučeně na sdíleném disku).
              Při uložení se do ní zkušebně zapíše a hned smaže malý soubor. Objednávky se ukládají do podsložek podle dne; sdílí-li složku více témat, má každé navíc svou podsložku (např. pejsci).
            </span>
          </div>
          <LibraryEditor slug={t.slug} url={url} />
          <AiIdeasEditor key={JSON.stringify(t.ai || {})} t={t} busy={!!busy} onSave={ai => save({ ai }, 'ai', 'Nápady pro AI uloženy.')} />
          <SeoEditor key={JSON.stringify([t.seoTitle, t.seoDescription, t.seoH1, t.seoKeywords, t.seoByLang])} initial={t} busy={!!busy} onSave={f => save(f as Record<string, unknown>, 'seo', 'Texty pro vyhledávače uloženy.')} />
          <div className="flex items-center justify-between gap-3 flex-wrap">
            <span className={hint}>
              Interní ID: <span className="font-mono">{t.instanceId}</span>
              {t.createdAt ? ` · založeno ${new Date(t.createdAt).toLocaleString('cs-CZ')}` : ''}{t.createdBy ? ` (${t.createdBy})` : ''}
              {t.copiedFrom ? ` · kopie z ${t.copiedFrom}` : ''}
            </span>
            <button type="button" className={btn} disabled={!!busy} onClick={() => save({ enabled: !t.enabled }, 'enabled', t.enabled ? 'Téma vypnuto – adresa ukazuje hlavní web.' : 'Téma zapnuto.')}>{t.enabled ? 'Vypnout téma' : 'Zapnout téma'}</button>
          </div>
        </div>
      )}
      {msg && <p className={`mt-2 text-[11px] font-bold ${msg.ok ? 'text-green-700' : 'text-red-700'}`}>{msg.text}</p>}
    </div>
  );
};

const ThemesAdmin: React.FC<{ isSuper?: boolean }> = () => {
  // Témata zakládá a mění každý administrátor (přístup do administrace už vyžaduje roli a 2FA).
  const isSuper = true;
  const [data, setData] = useState<{ base: string; serviceAccount: string; themes: Theme[] } | null>(null);
  const [err, setErr] = useState<string | null>(null);
  const [name, setName] = useState('');
  const [slug, setSlug] = useState('');
  const [slugTouched, setSlugTouched] = useState(false);
  const [copyFrom, setCopyFrom] = useState('shared');
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState<{ ok: boolean; text: string } | null>(null);

  const [reloadKey, setReloadKey] = useState(0);
  const load = () => adminFetch('/api/admin/themes').then(d => { setData(d); setErr(null); setReloadKey(k => k + 1); }).catch(e => setErr(e.message));
  useEffect(() => { load(); }, []);

  const create = async () => {
    setBusy(true); setMsg(null);
    try {
      const r = await adminFetch<{ copied: number; reused: boolean }>('/api/admin/themes', { body: { name: name.trim(), slug, copyFrom } });
      setMsg({ ok: true, text: r.reused ? `Téma /${slug} založeno, použila se dříve uložená data.` : `Téma /${slug} založeno${copyFrom === 'empty' ? ' (prázdné)' : copyFrom === 'shared' ? ' s vlastní prázdnou knihovnou a sdílenými složkami hlavní adresy' : `, zkopírováno ${r.copied} položek knihovny`}. Nastavte mu složku objednávek a nahrajte vlastní motivy.` });
      setName(''); setSlug(''); setSlugTouched(false);
      load();
    } catch (e: any) { setMsg({ ok: false, text: e.message }); }
    finally { setBusy(false); }
  };

  return (
    <div className="space-y-6">
      <div className="space-y-2">
        <h5 className="rda-h5">Témata webu</h5>
        <p className="text-[11px] text-gray-600">
          Každé téma je samostatná verze konfigurátoru na vlastní adrese (např. {window.location.origin}/pejsci) s vlastním názvem, logem,
          knihovnou ikon, pozadí a šablon, nástroji pro zákazníka, ceníkem, návratovou URL a složkou objednávek.
          Administrátoři, AI modely a limity, tisk a barvy, zabezpečení a provoz jsou společné a nastavují se tady na hlavní adrese.
        </p>
      </div>

      {err && <p className="text-[11px] font-bold text-red-700">{err}</p>}
      {!data && !err && <p className="text-[11px] text-gray-500">Načítám…</p>}
      <VersionsTable reloadKey={reloadKey} />
      <MainSeoCard onChanged={() => setReloadKey(k => k + 1)} />
      {data && data.themes.length === 0 && <p className="text-[11px] text-gray-500">Zatím žádné téma.</p>}
      {data && data.themes.map(t => <ThemeRow key={`${t.slug}:${t.driveFolderId}:${t.enabled}:${t.name}:${t.driveOk}`} t={t} isSuper={isSuper} serviceAccount={data.serviceAccount} onChanged={load} />)}

      {isSuper ? (
        <div className={box}>
          <span className={label}>Nové téma</span>
          <div className="grid gap-2 mt-2 sm:grid-cols-2">
            <div>
              <input className={txt} value={name} maxLength={80} placeholder="Název, např. Pejsci" onChange={e => { setName(e.target.value); if (!slugTouched) setSlug(toSlug(e.target.value)); }} />
            </div>
            <div className="flex items-center gap-1">
              <span className="text-[11px] font-bold font-mono text-gray-500 whitespace-nowrap">/</span>
              <input className={`${txt} font-mono`} value={slug} maxLength={40} placeholder="pejsci" onChange={e => { setSlugTouched(true); setSlug(toSlug(e.target.value)); }} />
            </div>
          </div>
          <div className="mt-2">
            <select className={txt} value={copyFrom} onChange={e => setCopyFrom(e.target.value)}>
              <option value="shared">Nastavení z hlavní adresy, vlastní knihovna + sdílené složky (doporučeno)</option>
              <option value="">Zkopírovat nastavení i celou knihovnu z hlavní adresy</option>
              {data?.themes.map(t => <option key={t.slug} value={t.slug}>Zkopírovat z tématu {t.name} (/{t.slug})</option>)}
              <option value="empty">Začít prázdné (bez knihovny)</option>
            </select>
          </div>
          <div className="flex items-center justify-between gap-3 mt-3 flex-wrap">
            <span className={hint}>Adresa: {window.location.origin}/{slug || '…'} · jen malá písmena bez diakritiky, číslice a pomlčky.</span>
            <button type="button" className={btnDark} disabled={busy || !name.trim() || slug.length < 2} onClick={create}>{busy ? 'Zakládám…' : 'Založit téma'}</button>
          </div>
        </div>
      ) : null}
      {msg && <p className={`text-[11px] font-bold ${msg.ok ? 'text-green-700' : 'text-red-700'}`}>{msg.text}</p>}
    </div>
  );
};

export default ThemesAdmin;
